import React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '../../utils/cn';

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  description?: string;
  className?: string;
  id?: string;
  /**
   * Accessible name for a switch used WITHOUT the `label` prop — an icon-only or
   * row-inline toggle. Without it such a switch is announced as an unnamed control.
   */
  'aria-label'?: string;
  /**
   * Names the switch for tests. Forwarded onto the Radix root, i.e. onto the element with
   * `role="switch"` — so a test clicks the control itself, not a wrapper that may not toggle it.
   */
  'data-testid'?: string;
}

const Switch = React.forwardRef<
  React.ComponentRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ checked, onCheckedChange, disabled, label, description, className, id, ...props }, ref) => {
  const switchId = id || React.useId();

  const switchElement = (
    <SwitchPrimitive.Root
      ref={ref}
      id={switchId}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={cn(
        'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-primary data-[state=unchecked]:bg-muted',
        className
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none block h-5 w-5 rounded-full bg-card shadow-sm transition-transform duration-200',
          'data-[state=checked]:ltr:translate-x-5 data-[state=checked]:rtl:-translate-x-5',
          'data-[state=unchecked]:translate-x-0'
        )}
      />
    </SwitchPrimitive.Root>
  );

  if (!label) return switchElement;

  return (
    <div className="flex items-center gap-3">
      {switchElement}
      <div className="flex flex-col">
        <label
          htmlFor={switchId}
          className={cn(
            'text-sm font-medium text-foreground cursor-pointer',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {label}
        </label>
        {description && (
          <span className="text-xs text-muted-foreground">{description}</span>
        )}
      </div>
    </div>
  );
});
Switch.displayName = 'Switch';

export { Switch };
export default Switch;
